/* eslint-disable no-console */
import {
  AtSymbolIcon,
  BookmarkIcon,
  CalculatorIcon,
  ClipboardDocumentIcon,
  IdentificationIcon,
  MegaphoneIcon,
  PlusCircleIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import {
  ArrowPathIcon,
  InformationCircleIcon,
  PencilSquareIcon,
  UserCircleIcon,
} from '@heroicons/react/24/solid';
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Container, Tooltip } from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import BackButton from '@straico-ui/commons/BackButton';
import LabelIcon from '@straico-ui/forms/LabelIcon';
import SmartSwitchInput from '@straico-ui/forms/SmartSwitchInput';
import TextInput from '@straico-ui/forms/TextInput';
import useChatStore from 'Components/Chat/store';
import Modal from 'Components/Common/Modal/Modal';
import ModalWithTextField from 'Components/Common/Modal/ModalApi';
import PercentageHandler from 'Components/Common/PercentageHandler/PercentageHandler';
import LLMCombos from 'Components/Forms/LLMCombos';
import Loader from 'Components/Loader';
import useClipboardComponent from 'Hooks/useClipboardComponent';
import { inject, observer } from 'mobx-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DraggableList from 'react-draggable-list';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import useThemeStore from 'Theme/store';
import { cn } from 'utils/styles';
import * as yup from 'yup';
import ModelsModal from '../Features/ModelsModal';
import ComboItemTemplate from './ComboItemTemplate';

const UserPreferences = ({ store }) => {
  /** THEME */
  const { theme } = useThemeStore();

  /** PROFILE INFO */
  const {
    lname,
    fname,
    email,
    default_model,
    nickname_fpr,
    plan,
    api_key,
    multi_default_models,
    wordcap_limits,
  } = store.profile;
  const getImageProfile = () => store.profile.avatar.svg;

  /** STATES */
  const wordcapLimitsSection = useRef(null);
  const [models, setModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [pricingMethod, setPricingMethod] = useState(
    store.profile.pricing_method
  );
  const [firstname, setFirstName] = useState(fname);
  const [lastname, setLastName] = useState(lname);
  const [wordcapLimits, setWordcapLimits] = useState(
    getLimitValues(wordcap_limits)
  );
  const [iconData, setIconData] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loadingResetApiKey, setLoadingResetApiKey] = useState(false);
  const [modelsDialogOpen, setModelsDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState({
    selectedModels: [],
    handleSelectModels: () => {},
  });
  const [newlyCreatedComboId, setNewlyCreatedComboId] = useState(null);

  const navigate = useNavigate();
  const { copy } = useClipboardComponent();

  const {
    combos,
    setModelsList,
    fetchCombos,
    updateCombo,
    selectedCombo,
    setSelectedCombo,
    updateSelectedModels,
  } = useChatStore();

  const location = useLocation();

  useEffect(() => {
    fetchCombos(); // from useChatStore
  }, [fetchCombos]);

  useEffect(() => {
    if (location.hash) {
      const section = document.querySelector(location.hash);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth' });
        section.focus();
      }
    }
  }, [location.hash]);

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    register,
    formState: { errors },
  } = useForm({
    defaultValues: {
      api_key: api_key,
      email: email,
      fname: firstname,
      lname: lastname,
      default_model: [default_model],
      nickname_fpr: nickname_fpr
        ? `https://platform.straico.com/signup?fpr=${nickname_fpr}`
        : 'https://platform.straico.com/signup?fpr=',
      multi_default_models:
        multi_default_models.length > 0
          ? multi_default_models
          : [default_model],
      wordcap_limits: wordcap_limits,
    },
    resolver: yupResolver(schemaValidationSignUp),
  });

  // Helper function to check if a string is a valid URL
  function isURL(str) {
    const urlRegex = /^(ftp|http|https):\/\/[^ "]+$/;
    return urlRegex.test(str);
  }

  // Helper function to split the path and extract the 'fpr' value
  function getFprValueFromURL(url) {
    if (!isURL(url)) return null;
    const newUrl = new URL(url);
    const fpr = newUrl.searchParams.get('fpr');
    return fpr;
  }

  const viabilityMultiDefault = (multi_default_models) => {
    // Filter models based on IDs in multi_default_models
    const defaultModels = models.filter((model) =>
      multi_default_models.includes(model.id)
    );

    // Filter default models that are enabled
    const enabledModels = defaultModels.filter((model) => model.enabled);

    if (enabledModels.length > 0) {
      return enabledModels.map((model) => model.id);
    } else {
      // If no enabled default models, fallback based on conditions
      if (!default_model.enabled) {
        if (store.profile.plan === 'free') {
          return ['65f377dcaa4feb73a4ae62ac'];
        } else {
          return ['6620346cecf94d7b874019f2'];
        }
      } else {
        return [default_model.id];
      }
    }
  };

  useEffect(() => {
    if (!combos.length) {
      fetchCombos();
    }
  }, [combos, fetchCombos]);

  const onSubmit = async (data) => {
    setSubmitLoading(true);
    try {
      let fprValue = null;
      if (data.nickname_fpr) fprValue = getFprValueFromURL(data.nickname_fpr);

      const res = await store.api.post(`/user/update-user`, {
        lname: data.lname,
        fname: data.fname,
        default_model: data.default_model,
        multi_default_models: data.multi_default_models,
        nickname_fpr: fprValue,
        wordcap_limits: getValues('wordcap_limits'),
        pricing_method: pricingMethod,
      });
      localStorage.removeItem('currentModel');

      if (res.status === 200) {
        localStorage.setItem('profile', JSON.stringify(res.data));
        setFirstName(res.data.fname);
        setLastName(res.data.lname);
        store.profile.pricing_method = res.data.pricing_method;
        setSubmitLoading(false);
        store.profile.nickname_fpr = res.data.nickname_fpr;
        store.profile.wordcap_limits = res.data.wordcap_limits;
        toast.success('Profile updated');
      } else {
        toast.success('Updating error');
      }
    } catch (error) {
      toast.error(
        'Updating error, check that your referral link is valid or change it, to a unique one'
      );
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleCopy = (e) => {
    try {
      e.preventDefault();
      copy(store.profile.api_key);
      toast.success('API key copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const handleCopy2 = (e, apiKeyValue) => {
    try {
      e.preventDefault();
      copy(apiKeyValue);
      toast.success('API key copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const resetApiKey = async () => {
    setLoadingResetApiKey(true);
    try {
      const res = await store.api.post(`/user/change-api-key`, {});
      if (res.status === 200) {
        store.profile.api_key = res.data.api_key;
        setValue('api_key', res.data.api_key);
        toast.success('API key updated');
      } else {
        toast.success('Updating error');
      }
    } catch (error) {
      toast.error('Updating error');
    } finally {
      setLoadingResetApiKey(false);
    }
  };

  const closeModal = () => setIsOpen(false);
  const openModal = () => setIsOpen(true);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);
  const [apiKeys, setApiKeys] = useState([]);

  const fetchApiKeys = async () => {
    try {
      const response = await store.api.get('key/api-keys');
      setApiKeys(response.data);
    } catch (error) {
      console.error('Error fetching API Keys:', error);
    }
  };

  const deleteKeyById = async (keyId) => {
    try {
      const response = await store.api.delete(`key/${keyId}`);
      if (response.status === 200) {
        fetchApiKeys();
        toast.success('Key deleted');
      } else {
        console.warn('Could not delete key. Response status:', response.status);
      }
    } catch (error) {
      toast.error('Error deleting key');
    }
  };

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const changeLimits = (newValues) => {
    const currentValues = getValues('wordcap_limits');
    setWordcapLimits(newValues);
    setValue('wordcap_limits', { ...currentValues, ...newValues });
  };

  const addNewKey = async (textFieldValue) => {
    try {
      const createKeyResponse = await store.api.post('key/create', {
        name: textFieldValue,
      });
      if (createKeyResponse.status === 201) {
        fetchApiKeys();
        toast.success('New key created', createKeyResponse.data);
      } else {
        throw new Error('Error creating new key');
      }
    } catch (error) {
      toast.error('AddNewKey error', error.message);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const response = await store.api.get(`/model`);
        let availableModels = response.data.models.filter(
          (model) => model.enabled
        );
        let transformedModels;
        if (store.profile.plan == 'freeTos') {
          const res = await store.api.get('/user/constant', {
            params: {
              type: 'princing_plans',
            },
          });
          let freePlanModels = []; // Array to store models allowed for free users
          freePlanModels = res.data.rules[0].models;
          const modelsInFreePlan = []; // Array to store models available for free users
          const otherModels = []; // Array to store models not available for free users
          availableModels.forEach((model) => {
            if (freePlanModels.includes(model.model)) {
              modelsInFreePlan.push(model);
            } else {
              otherModels.push(model);
            }
          });
          transformedModels = modelsInFreePlan.map((model) => ({
            id: model._id,
            title: model.name,
          }));
        } else {
          transformedModels = response.data.models.map((model) => ({
            id: model._id,
            title: model.name,
          }));
        }
        control.multi_default_models =
          viabilityMultiDefault(multi_default_models);
        setModels(transformedModels);
        setLoadingModels(false);
      } catch (error) {
        console.log(error);
      }
    })();
  }, []);

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const keys = Object.keys(errors);
      keys.forEach((key) => {
        toast.error(errors[key].message);
      });
    }
  }, [errors]);

  const [combosList, setCombosList] = useState([]);

  const [allModels, setAllModels] = useState([]);
  useEffect(() => {
    const fetchAllModels = async () => {
      try {
        const response = await store.api.get(`/model`);
        setAllModels(response.data.models || []);
      } catch (error) {
        console.error('Error fetching all models:', error);
      }
    };

    fetchAllModels();
  }, []);

  useEffect(() => {
    if (!combos.length) {
      fetchCombos();
    } else {
      const formattedCombos = combos.map((combo) => ({
        id: combo._id,
        name: combo.name,
        llms: combo.llms,
        position: combo.position,
        onSelect: () => updateCombo(combo._id),
        store,
        theme,
        allModels,
      }));
      setCombosList(formattedCombos);
    }
  }, [combos]);

  const handleMoveEnd = useCallback(
    async (newList) => {
      const updatedList = newList.map((item, index) => ({
        ...item,
        position: index + 1,
        isDefault: index === 0,
      }));

      setCombosList(updatedList);
      try {
        // Helper function to retry API calls with exponential backoff
        const retryApiCall = async (apiCall, maxRetries = 3) => {
          let lastError;
          for (let attempt = 0; attempt < maxRetries; attempt++) {
            try {
              return await apiCall();
            } catch (error) {
              console.warn(
                `API call failed (attempt ${attempt + 1}/${maxRetries}):`,
                error
              );
              lastError = error;
              // Exponential backoff: wait longer between each retry
              await new Promise((resolve) =>
                setTimeout(resolve, 300 * Math.pow(2, attempt))
              );
            }
          }
          throw lastError; // If we've exhausted all retries, throw the last error
        };

        // Update the backend with retry logic
        await Promise.all(
          updatedList.map((item) =>
            retryApiCall(() =>
              store.api.put(`/combos/llm-combos/${item.id}`, {
                name: item.name,
                llms: item.llms,
                isDefault: item.isDefault,
                position: item.position,
                isSelected: item.isSelected,
              })
            )
          )
        );

        // Add a small delay to ensure backend updates are processed before fetching
        await new Promise((resolve) => setTimeout(resolve, 500));
        await fetchCombos();

        const firstCombo = updatedList[0];
        if (firstCombo) {
          setSelectedCombo(firstCombo);

          const selectedModels = firstCombo.llms
            .map((llmId) => allModels.find((model) => model._id === llmId))
            .filter(Boolean)
            .sort((a, b) => a.abbreviation.localeCompare(b.abbreviation));

          updateSelectedModels(() => selectedModels);
          localStorage.setItem('currentModels', JSON.stringify(selectedModels));
        }
      } catch (error) {
        console.error('Error updating combo positions and models:', error);
        toast.error('Failed to update combo positions. Please try again.');
      }
    },
    [
      store,
      fetchCombos,
      allModels,
      updateSelectedModels,
      setCombosList,
      setSelectedCombo,
    ]
  );

  const memoizedCombosList = useMemo(() => {
    // Make a copy and ensure all positions are valid
    const validatedCombos = [...combosList].map((combo) => {
      // Ensure position is a number
      return {
        ...combo,
        position: typeof combo.position === 'number' ? combo.position : 999,
      };
    });

    // Sort by position and re-assign sequential positions
    return validatedCombos
      .sort((a, b) => a.position - b.position)
      .map((item, index) => ({
        ...item,
        position: index + 1,
        isDefault: index === 0,
      }));
  }, [combosList]);

  const openModelsDialog = useCallback(
    ({ selectedModels, handleSelectModels }) => {
      setDialogData({ selectedModels, handleSelectModels });
      setModelsDialogOpen(true);
    },
    []
  );

  const cloneCombo = useCallback(
    async ({ baseName, validModels, combos }) => {
      let newName = `${baseName}`;
      let copyNumber = 2;
      while (
        combos.some(
          (combo) => combo.name.toLowerCase() === newName.toLowerCase()
        )
      ) {
        newName = `${baseName} (copy ${copyNumber})`;
        copyNumber++;
      }

      try {
        // 1) Crear la copia en el backend
        const newCombo = await store.api.post('/combos/llm-combos', {
          name: newName,
          llms: validModels.map((model) => model._id),
          isDefault: false,
          isSelected: true,
          isStraicoDefault: false,
        });

        const createdCombo = newCombo.data.combo;

        // Verificar que el _id del clon sea único
        if (combos.some((combo) => combo._id === createdCombo._id)) {
          console.warn(
            '[cloneCombo] Advertencia: El _id del combo clonado ya existe en la lista.'
          );
        }

        // 2) Agregar el nuevo combo al estado global
        useChatStore.setState((state) => ({
          combos: [...state.combos, createdCombo],
        }));
        setSelectedCombo(createdCombo);

        // 3) Notificar que este combo es el recién creado para edición
        setNewlyCreatedComboId(createdCombo._id);

        return createdCombo;
      } catch (error) {
        console.error('[cloneCombo] Error creando copia del combo:', error);
        toast.error('Failed to create combo copy.');
        return null;
      }
    },
    [store]
  );

  const memoizedTemplate = useCallback(
    (templateProps) => {
      const { item } = templateProps;
      return (
        <ComboItemTemplate
          {...templateProps}
          allModels={allModels}
          openModelsDialog={openModelsDialog}
          onClone={cloneCombo}
          startEditing={item.id === newlyCreatedComboId}
        />
      );
    },
    [allModels, openModelsDialog, newlyCreatedComboId, cloneCombo]
  );

  const closeModelsDialog = useCallback(() => {
    setModelsDialogOpen(false);
  }, []);

  return (
    <div className="overflow-auto">
      <Container maxWidth="md" className="font-figtree">
        <BackButton
          text="Go back to user settings"
          onClick={() => navigate('/my-profile')}
        />
        <div
          className={cn('my-4 mb-10', {
            'text-white': theme === 'dark',
            'text-[#211E21]': theme !== 'dark',
          })}
        >
          <h1 className={cn('text-3xl font-bold')}>Your Preferences</h1>
          <p className="text-xl mt-2">
            Personalize your basic information and set up your preferred Straico
            experience.
          </p>
          {loadingModels ? (
            <div className="absolute inset-0 flex items-center justify-center h-full">
              <Loader active className="m-auto w-64 h-64 pr-10" />
            </div>
          ) : (
            <>
              <div className="mx-auto flex flex-row justify-between items-center mt-3">
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <div className="w-16 h-16 rounded-full overflow-hidden">
                      {store.profile.avatar.svg ? (
                        <div className="avatar">
                          <div className="w-16 h-16 rounded-full overflow-hidden">
                            <div
                              dangerouslySetInnerHTML={{
                                __html: getImageProfile(),
                              }}
                            />
                          </div>
                        </div>
                      ) : (
                        <UserCircleIcon className="w-16 h-16transition text-cool-gray" />
                      )}
                    </div>

                    <div
                      className="absolute -bottom-2 -right-1 rounded-full bg-nue-blue w-6 h-6 flex items-center justify-center"
                      onClick={() => {
                        navigate('/my-avatar');
                      }}
                    >
                      <PencilSquareIcon className="cursor-pointer w-4 h-4 text-white bg-nue-blue rounded-full" />
                    </div>
                  </div>
                  <div className="flex flex-col">
                    <div
                      className={`text-lg font-bold uppercase ${
                        theme == 'dark'
                          ? 'dark:text-crystal-bell'
                          : 'text-raisin-black'
                      }`}
                    >{`${firstname} ${lastname}`}</div>
                    <div
                      className={`text-sm font-normal ${
                        theme == 'dark'
                          ? 'dark:text-crystal-bell'
                          : 'text-raisin-black'
                      }`}
                    >
                      {email}
                    </div>
                  </div>
                </div>
              </div>

              <form
                className="mx-auto flex flex-col justify-between space-y-6 my-10"
                onSubmit={handleSubmit(onSubmit)}
              >
                <section className="flex flex-col gap-6 p-5 rounded-lg rounded-xl border border-gray-500/50">
                  <p className="text-2xl font-bold capitalize">
                    Identity Essentials
                  </p>
                  <div className="flex flex-col gap-5 mb-4">
                    <div className="flex items-center">
                      <div className="w-1/5">
                        <LabelIcon icon={AtSymbolIcon} text="Email" />
                      </div>
                      <div className="w-4/5">
                        <TextInput
                          {...register('email')}
                          name="email"
                          type="email"
                          disabled
                        />
                      </div>
                    </div>
                    <div className="flex items-center w-full">
                      <div className="w-1/5 mr-2">
                        <LabelIcon
                          icon={IdentificationIcon}
                          text="True Identity"
                        />
                      </div>
                      <div className="flex items-center gap-4 w-4/5">
                        <div className="w-2/4">
                          <TextInput
                            {...register('fname')}
                            name="fname"
                            type="text"
                            placeholder="First Name"
                          />
                        </div>
                        <div className="w-2/4">
                          <TextInput
                            {...register('lname')}
                            name="lname"
                            type="text"
                            placeholder="Last Name"
                          />
                        </div>
                      </div>
                    </div>
                    {/* <div className="flex items-center">
                      <div className="w-1/5">
                        <LabelIcon icon={BoltIcon} text="Hero alias" />
                      </div>
                      <div className="w-4/5 flex">
                        <TextInput placeholder="web devoloper" />
                        <LoadingButton
                          variant="contained"
                          color="primary"
                          startIcon={<SparklesIcon className="size-5" />}
                          sx={{
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '14px',
                            textTransform: 'initial',
                            width: '100px',
                            padding: '2px',
                            borderRadius: '10px',
                            ml: 3,
                          }}
                        >
                          Use AI
                        </LoadingButton>
                      </div>
                    </div> */}
                  </div>
                </section>
                <div className="h-4" />
                <section className="p-5 rounded-lg rounded-xl border border-gray-500/50">
                  <p className="text-2xl font-bold capitalize mb-4">
                    Boost Your Influence
                  </p>
                  <p>
                    Save <strong>your personal referral link</strong> here, and
                    it will become a silent partner in every chat and template
                    you share.{' '}
                    <a
                      className="text-violet-blue cursor-pointer"
                      onClick={openModal}
                    >
                      Need help finding your referral link?
                    </a>
                  </p>

                  <div>
                    <div className="flex items-center">
                      <div className="w-1/5 mr-2">
                        <LabelIcon icon={MegaphoneIcon} text="Referral Link" />
                      </div>
                      <div className="w-4/5">
                        <TextInput
                          name="nickname_fpr"
                          {...register('nickname_fpr')}
                        />
                      </div>
                    </div>
                  </div>

                  <p>
                    Not yet signed up? Embark on the path to monetizing your
                    influence by joining our{' '}
                    <a
                      className="text-violet-blue cursor-pointer"
                      href="https://straico.com/affiliate/"
                      target="__blank"
                    >
                      affiliate program.
                    </a>
                  </p>
                </section>
                <div className="h-4" />
                <section
                  id="personalizeAI"
                  className="flex flex-col gap-6 p-5 rounded-xl border border-gray-500/50"
                >
                  <p className="text-2xl font-bold capitalize">
                    Personalize Your AI Interactions
                  </p>
                  <div className="flex flex-col gap-3">
                    <p className="font-bold capitalize">Default AI Models</p>
                    <p className="text-sm">
                      Your interactions with AI should be as individual as you
                      are. Create LLM combinations with{' '}
                      <strong>
                        up to 4 default Large Language Models (LLMs)
                      </strong>
                      , crafting a personalized path of discovery and
                      interaction for all your chats.
                    </p>
                    <p className="text-sm">
                      If you want to modify, create, or delete any combo, you
                      can do so in the section below. To set the default combo
                      that loads when you start a new chat, simply{' '}
                      <strong>
                        drag your preferred option to the top of the list
                      </strong>
                      — it'll be automatically ready for you every time you
                      enter.
                    </p>

                    <LLMCombos
                      store={store}
                      combos={combos}
                      selectedCombo={selectedCombo}
                      onSelectCombo={updateCombo}
                      theme={theme}
                      allModels={allModels}
                    />
                    <div style={{ position: 'relative' }}>
                      {' '}
                      {combosList.length > 0 ? (
                        <DraggableList
                          list={memoizedCombosList}
                          itemKey="id"
                          template={memoizedTemplate}
                          onMoveEnd={handleMoveEnd}
                          padding={10}
                          bounds="parent"
                          constrainDrag={true}
                        />
                      ) : (
                        <p>No combos available</p>
                      )}
                    </div>
                  </div>
                  {/* Pricing method description section */}
                  <div className="flex flex-col gap-3">
                    <p id="pricing-settings" className="font-bold capitalize">
                      Pricing mode
                    </p>
                    <div className="flex items-center gap-2">
                      <div className="w-4/5">
                        <div className="flex items-center gap-4">
                          <span
                            className={`text-sm ${
                              pricingMethod === 'perMessage'
                                ? 'font-bold'
                                : 'font-normal'
                            }`}
                          >
                            Pricing per Message
                          </span>

                          <SmartSwitchInput
                            checked={pricingMethod === 'perWord'}
                            onChange={(event) => {
                              const newMethod = event.target.checked
                                ? 'perWord'
                                : 'perMessage';
                              setPricingMethod(newMethod);
                              setValue('pricing_method', newMethod);
                              store.profile.pricing_method = newMethod;
                            }}
                          />

                          <span
                            className={`text-sm ${
                              pricingMethod === 'perWord'
                                ? 'font-bold'
                                : 'font-normal'
                            }`}
                          >
                            Pricing per Word
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 text-sm">
                      {store.profile.pricing_method === 'perMessage' ? (
                        <p>
                          <strong>Pricing per Message</strong>: Every
                          interaction deducts coins based on the length of your
                          prompt and response, but with an upper cap. This
                          ensures predictable pricing, even in long
                          conversations where word accumulation previously
                          caused costs to escalate dramatically.{' '}
                          <a
                            href="https://straico.com/pricing-modes/"
                            className="text-violet-blue cursor-pointer"
                          >
                            Check this article
                          </a>{' '}
                          for more information.
                        </p>
                      ) : (
                        <div className="space-y-2">
                          <p>
                            <strong>Per Word pricing</strong>: Coins will be
                            deducted depending on the number of words of the
                            prompt, output, conversation and context. You'll see
                            color-coded alerts to help you understand prompt
                            costs at a glance:
                          </p>
                          <ul className="list-disc ml-5 mt-2">
                            <li>
                              <span className="text-[#9cb773] font-semibold">
                                Green
                              </span>
                              : Low cost (under 60 coins)
                            </li>
                            <li>
                              <span className="text-[#e3c06f] font-semibold">
                                Yellow
                              </span>
                              : Moderate cost (60-199 coins)
                            </li>
                            <li>
                              <span className="text-[#e68e5b] font-semibold">
                                Orange
                              </span>
                              : High cost (200+ coins)
                            </li>
                          </ul>
                          <p>
                            <a
                              href="https://straico.com/pricing-modes/"
                              className="text-violet-blue cursor-pointer"
                            >
                              Check this article
                            </a>{' '}
                            for more information.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-3">
                    <p className="font-bold capitalize">Wordcap Limit</p>
                    <p className="text-sm">
                      Manage{' '}
                      <strong>
                        how many words are included in your requests
                      </strong>{' '}
                      to optimize your Straico coin usage. Recommended limit is
                      1,000 words per <strong>request</strong>. If you set the
                      limit to 0, the wordcap will be disabled, and word
                      capacity will then depend on the model's specific limits.
                    </p>
                    <div className="flex items-center w-full">
                      <div className="w-1/5 mr-2">
                        <LabelIcon
                          icon={CalculatorIcon}
                          text="Words Per Request"
                        />
                      </div>
                      <div className="w-4/5">
                        <div className="w-fit">
                          <TextInput
                            {...register('wordcap_limits.words')}
                            placeholder="1000"
                            type="number"
                            name="wordcap_limits.words"
                            min={0}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <p>
                    Use the slider below to configure the percentage
                    distribution for each component of the Wordcap Limit:
                  </p>
                  <PercentageHandler
                    attributes={wordcapLimits}
                    words={getValues('wordcap_limits').words}
                    onChange={changeLimits}
                  />
                </section>
                <div className="h-4" />
                {store.profile.api_key && (
                  <div className="p-5 rounded-xl border border-gray-500/50">
                    {/* Sección: Connect with Straico API */}
                    <section className="flex flex-col gap-6">
                      <div className="flex justify-between items-center">
                        <p className="text-2xl font-bold capitalize">
                          Connect with Straico API
                        </p>
                        <LoadingButton
                          onClick={handleOpenModal}
                          variant="contained"
                          color="primary"
                          startIcon={
                            <PlusCircleIcon
                              style={{
                                width: '20px',
                                height: '20px',
                                border: '1.6px',
                              }}
                            />
                          }
                          sx={{
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '14px',
                            textTransform: 'initial',
                            width: { xs: '225px', sm: '216px' },
                            height: { xs: '48px', sm: '40px' },
                            padding: '2px',
                            borderRadius: '10px',
                            ml: 3,
                            typography: {
                              fontFamily: 'Figtree, sans-serif',
                              fontWeight: 600,
                              fontSize: '16px',
                              lineHeight: '24px',
                            },
                            '& .MuiButton-startIcon': {
                              ml: { xs: 1, sm: 0 },
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            },
                          }}
                        >
                          Create New Api Key
                        </LoadingButton>
                        <ModalWithTextField
                          open={isModalOpen}
                          onClose={handleCloseModal}
                          addNewKey={addNewKey}
                        />
                      </div>

                      <p>
                        Easily enhance your projects with our{' '}
                        <strong>user-friendly API.</strong>
                        Supercharge your tools with AI capabilities for faster
                        and easier work. Visit our{' '}
                        <a
                          className="text-violet-blue cursor-pointer"
                          href="https://documenter.getpostman.com/view/5900072/2s9YyzddrR"
                          target="__blank"
                        >
                          API Documentation{' '}
                        </a>{' '}
                        for step-by-step guidance and support. Straico API
                        Documentation.
                      </p>
                    </section>
                    <div className="mt-6">
                      <TableContainer
                        component={Paper}
                        sx={{
                          borderRadius: '14px',
                          bgcolor: (theme) =>
                            theme.palette.mode === 'light'
                              ? '#ededf2'
                              : '#1d1c1e',
                        }}
                      >
                        <Table sx={{ minWidth: 650 }} aria-label="simple table">
                          <TableHead>
                            <TableRow className="border-b border-[#b0b0b0] dark:border-[#39383a]">
                              <TableCell
                                align="center"
                                style={{ width: '168px', height: '24px' }}
                              >
                                <strong>Name</strong>
                              </TableCell>
                              <TableCell
                                align="center"
                                style={{ width: '281px' }}
                              >
                                <strong>Key</strong>
                              </TableCell>
                              <TableCell
                                align="center"
                                style={{ width: '110px' }}
                              >
                                <strong>Created</strong>
                              </TableCell>
                              <TableCell
                                align="center"
                                style={{ width: '110px' }}
                              >
                                <strong>Last Used</strong>
                              </TableCell>
                              <TableCell
                                align="center"
                                style={{ width: '103px' }}
                              >
                                <strong>Active</strong>
                              </TableCell>
                              <TableCell
                                align="center"
                                style={{ width: '100px' }}
                              ></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {/* Fila de la Main API Key */}
                            <TableRow align="center">
                              <TableCell align="center">
                                <div className="flex items-center justify-center gap-2">
                                  <Tooltip
                                    title="This is your main API key. It is used to authenticate your account with Straico's API."
                                    arrow
                                    placement="left"
                                  >
                                    <InformationCircleIcon className="text-cool-gray w-5 h-5 dark:text-sonic-silver" />
                                  </Tooltip>
                                  <span>Main API Key</span>
                                </div>
                              </TableCell>
                              <TableCell align="center">
                                <div
                                  className={`${
                                    theme === 'dark'
                                      ? 'bg-[#323232]'
                                      : 'bg-[#e1e1e5]'
                                  }`}
                                  style={{
                                    position: 'relative',
                                    width: '281px',
                                    height: '36px',
                                    borderRadius: '12px',
                                    border: '2px solid',
                                    paddingTop: '14px',
                                    paddingLeft: '14px',
                                    paddingRight: '40px',
                                    paddingBottom: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    borderColor: '#787878',
                                  }}
                                >
                                  <input
                                    type="text"
                                    {...register('api_key')}
                                    name="api_key"
                                    readOnly
                                    style={{
                                      flex: 1,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      border: 'none',
                                      outline: 'none',
                                      padding: 0,
                                      margin: 0,
                                    }}
                                    className={`w-full border ${
                                      theme === 'dark'
                                        ? 'bg-[#323232] text-crystal-bell'
                                        : 'bg-[#e1e1e5] text-raisin-black'
                                    }  border-platinum border-solid p-[8px] rounded-md focus:border-violet-blue`}
                                  />
                                  <div
                                    style={{
                                      position: 'absolute',
                                      right: '8px',
                                      top: '50%',
                                      transform: 'translateY(-50%)',
                                    }}
                                  >
                                    <ClipboardDocumentIcon
                                      className="cursor-pointer w-5 h-5"
                                      onClick={handleCopy}
                                    />
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell align="center">
                                {new Date(
                                  store.profile.created
                                ).toLocaleString()}
                              </TableCell>
                              <TableCell align="center">
                                <span>------</span>
                              </TableCell>
                              <TableCell align="center">
                                <span>Yes</span>
                              </TableCell>
                              <TableCell align="center">
                                <div className="flex items-center justify-center">
                                  <ArrowPathIcon
                                    onClick={resetApiKey}
                                    style={{
                                      width: '18px',
                                      height: '29px',
                                      cursor: 'pointer',
                                    }}
                                  />
                                </div>
                              </TableCell>
                            </TableRow>
                            {/* Dynamic API Keys */}
                            {apiKeys.map((apiKey) => (
                              <TableRow key={apiKey._id}>
                                <TableCell align="center">
                                  {apiKey.name}
                                </TableCell>
                                <TableCell align="center">
                                  <div
                                    className={`${
                                      theme === 'dark'
                                        ? 'bg-[#323232]'
                                        : 'bg-[#e1e1e5]'
                                    }`}
                                    style={{
                                      position: 'relative',
                                      width: '281px',
                                      height: '36px',
                                      borderRadius: '12px',
                                      border: '2px solid',
                                      paddingTop: '14px',
                                      paddingLeft: '14px',
                                      paddingRight: '40px',
                                      paddingBottom: '14px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      borderColor: '#787878',
                                    }}
                                  >
                                    <input
                                      name="api_key"
                                      value={apiKey.key}
                                      readOnly
                                      style={{
                                        flex: 1,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        border: 'none',
                                        outline: 'none',
                                        padding: 0,
                                        margin: 0,
                                      }}
                                      className={`w-full border ${
                                        theme === 'dark'
                                          ? 'bg-[#323232] text-crystal-bell'
                                          : 'bg-[#e1e1e5] text-raisin-black'
                                      }  border-platinum border-solid p-[8px] rounded-md focus:border-violet-blue`}
                                    />
                                    <div
                                      style={{
                                        position: 'absolute',
                                        right: '8px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                      }}
                                    >
                                      <ClipboardDocumentIcon
                                        className="cursor-pointer w-5 h-5"
                                        onClick={(e) =>
                                          handleCopy2(e, apiKey.key)
                                        }
                                      />
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell align="center">
                                  {new Date(apiKey.created).toLocaleString()}
                                </TableCell>
                                <TableCell align="center">
                                  {apiKey.lastUsed
                                    ? new Date(apiKey.lastUsed).toLocaleString()
                                    : 'Never'}
                                </TableCell>
                                <TableCell align="center">
                                  {apiKey.isActive ? 'Yes' : 'No'}
                                </TableCell>
                                <TableCell align="center">
                                  <div className="flex items-center justify-center">
                                    <TrashIcon
                                      onClick={() => deleteKeyById(apiKey._id)}
                                      style={{
                                        width: '18px',
                                        height: '20px',
                                        cursor: 'pointer',
                                      }}
                                    />
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </div>
                    <p className="mt-5">
                      Keep your API key secure - it's like a password. Lost it?
                      Contact us ASAP at{' '}
                      <a
                        className="text-violet-blue cursor-pointer"
                        href="mailto: hello@straico.com"
                        target="__blank"
                      >
                        hello@straico.com
                      </a>
                    </p>
                  </div>
                )}
                <div className="w-full pt-3">
                  <LoadingButton
                    variant="contained"
                    color="primary"
                    type="submit"
                    loading={submitLoading}
                    startIcon={<BookmarkIcon className="size-6" />}
                    sx={{
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '18px',
                      textTransform: 'initial',
                    }}
                  >
                    Save changes
                  </LoadingButton>
                </div>
              </form>
              {isOpen && (
                <Modal
                  open={isOpen}
                  noIconTitle
                  title="where to find your affiliate referral link?"
                  onClose={closeModal}
                >
                  <div className="flex flex-col gap-4 mt-[10px]">
                    <p
                      className={`
                      ${
                        theme == 'dark'
                          ? 'text-crystal-bell'
                          : 'text-raisin-black'
                      }
                    `}
                    >
                      This feature is exclusively for members of{' '}
                      <strong>Straico's affiliate program.</strong> For details
                      on the benefits and enrollment process,{' '}
                      <a
                        className="text-violet-blue cursor-pointer"
                        href="https://straico.com/affiliate/"
                        target="__blank"
                      >
                        click here.
                      </a>
                    </p>
                    <img
                      onClick={() =>
                        window.open(
                          'https://straico.firstpromoter.com/',
                          '__blank'
                        )
                      }
                      src="images/afiliate_images.png"
                      alt="affiliate"
                      className="cursor-pointer"
                      style={{
                        boxShadow: '0px 0px 6px 0px rgba(0, 0, 0, 0.15)',
                      }}
                    />
                    <p
                      className={`
                      ${
                        theme == 'dark'
                          ? 'text-crystal-bell'
                          : 'text-raisin-black'
                      }
                    `}
                    >
                      With your referral link embedded, every chat and template
                      URL you share has the potential to boost your earnings.
                      Start sharing and watch your rewards grow! 🚀
                    </p>
                  </div>
                </Modal>
              )}
            </>
          )}
        </div>
      </Container>
      {modelsDialogOpen && (
        <ModelsModal
          open={modelsDialogOpen}
          selectedModels={dialogData.selectedModels}
          handleCombos={dialogData.handleSelectModels}
          onClose={closeModelsDialog}
          nocapabilities={true}
        />
      )}
    </div>
  );
};

export default inject('store')(observer(UserPreferences));

const schemaValidationSignUp = yup.object({
  fname: yup
    .string()
    .required('First name is required')
    .matches(
      /^[A-Za-z0-9\s]+$/,
      'First name should only contain letters and numbers'
    ),
  lname: yup
    .string()
    .required('Last name is required')
    .matches(
      /^[A-Za-z0-9\s]+$/,
      'Last name should only contain letters and numbers'
    ),
  nickname_fpr: yup.string().url('Invalid URL').nullable(true),
});

const getLimitValues = (values) => {
  const temp = JSON.parse(JSON.stringify(values));
  delete temp.words;
  return temp;
};
